import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Tab,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";
import { EmptyState } from "@/components/ui";
import { theme } from "@/theme/theme";
import { DiscoveryRunLogs } from "../components/discovery-run-logs.tsx";
import { DiscoveryRunStateChip } from "../components/discovery-run-state-chip.tsx";
import { LiveView } from "../components/live-view.tsx";
import { useApiMutation } from "../hooks/use-api-mutation.ts";
import { useApiQuery } from "../hooks/use-api-query.ts";
import { useTypedQueryParams } from "../hooks/use-query-params.ts";
import type { DiscoveryRun, PersistedUserFlow, Persona } from "../types.ts";
import { formatDuration } from "../utils";
import { humanizeText } from "../utils/text.ts";

interface DiscoveryRunDetailsTabs {
  tab?: "live" | "recording" | "flows" | "issues";
}

export default function DiscoveryRunDetailsPage() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const userSelectedTabRef = useRef(false);

  const { enqueueSnackbar } = useSnackbar();

  const [finished, setFinished] = useState(false);

  const { params, setParam } = useTypedQueryParams<DiscoveryRunDetailsTabs>();

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: string) => {
      userSelectedTabRef.current = true;
      setParam("tab", newValue as DiscoveryRunDetailsTabs["tab"]);
    },
    [setParam],
  );

  const { runId } = useParams();

  const { data, isLoading } = useApiQuery<DiscoveryRun>(`/discovery-runs/${runId}`, {
    enabled: !!runId,
  });

  // Default to "live" only if run is running, otherwise default to "recording"
  const getDefaultTab = useCallback(() => {
    if (data && data.state !== "running") {
      return "recording";
    }
    return "live";
  }, [data]);

  const tab = params.tab ?? getDefaultTab();

  const { data: persona } = useApiQuery<Persona>(`/personas/${data?.persona_id}`, {
    enabled: !!data?.persona_id,
  });

  const { data: flows } = useApiQuery<PersistedUserFlow[]>(`/discovery-runs/${runId}/flows`, {
    enabled: !!runId,
  });

  const { mutate: cancelRun, isPending: isCancelling } = useApiMutation<void, void>(
    "POST",
    `/discovery-runs/${runId}/cancel`,
    {
      onSuccess: () => {
        enqueueSnackbar("Discovery run cancellation request sent successfully", {
          variant: "warning",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
      onError: (error) => {
        enqueueSnackbar(`Failed to cancel run: ${error.message}`, {
          variant: "error",
          anchorOrigin: {
            horizontal: "right",
            vertical: "bottom",
          },
        });
      },
    },
  );

  useEffect(() => {
    setFinished(false);
    userSelectedTabRef.current = false;
    // Set default tab if there's no existing tab in URL
    if (!params.tab) {
      setParam("tab", getDefaultTab());
    }
  }, [setParam, params.tab, getDefaultTab]);

  useEffect(() => {
    if (!data) return;
    if (userSelectedTabRef.current) return; // Don't auto-switch if user selected a tab
    if (finished) return;

    // If run finishes and user is on live tab, switch to recording
    if (data.state !== "running") {
      if (tab === "live") {
        setParam("tab", "recording");
      }
      setFinished(true);
    }
  }, [data?.state, tab, finished, setParam, data]);

  const duration = useMemo(() => {
    return formatDuration(data?.elapsed_seconds);
  }, [data?.elapsed_seconds]);

  if (isLoading) {
    return <CircularProgress />;
  }

  if (!data) {
    return <Typography>Discovery Run for id "{runId}" not found</Typography>;
  }

  return (
    <Box>
      <Stack direction="row" spacing={0} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DiscoveryRunStateChip state={data.state} />
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            divider={<Typography variant="body1">·</Typography>}
          >
            <Typography variant="body1">
              {new Date(data.started_at ?? data.created_at).toLocaleString()}
            </Typography>
            <Typography variant="body1">
              {data.ended_at ? new Date(data.ended_at).toLocaleString() : "Ongoing"}
            </Typography>
            <Typography variant="body1">{duration ?? "Calculating duration..."}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="flex-end">
          {data.external_id && (
            <Tooltip title="Cancel Discovery Run">
              <Button
                variant="outlined"
                color="warning"
                disabled={data.state !== "running" || isCancelling}
                loading={isCancelling}
                onClick={() => {
                  cancelRun({});
                }}
              >
                Cancel
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        <Card sx={{ flexGrow: 1, p: 2 }}>
          {data.intent ? (
            <Typography
              variant="body1"
              color="text.primary"
              sx={{
                whiteSpace: "pre-line",
                maxHeight: "80px",
                overflowY: "auto",
                width: "100%",
              }}
            >
              {data.intent}
            </Typography>
          ) : (
            <Typography variant="body1" color="text.secondary">
              No intent provided for this discovery run.
            </Typography>
          )}
          {data.intent && (
            <Typography variant="body2" color="text.secondary" align="right" sx={{ mt: 1 }}>
              {data.intent.length} characters and {data.intent.split("\n").length} lines
            </Typography>
          )}
        </Card>
        {persona && (
          <Tooltip title="View Persona Details">
            <Button
              variant="text"
              size="small"
              component={Link}
              to={`/personas/${persona.id}`}
              sx={{ minWidth: "unset" }}
            >
              <Typography variant="body2" noWrap>
                {persona.name}
              </Typography>
            </Button>
          </Tooltip>
        )}
      </Stack>
      <Grid container spacing={{ xs: 2 }}>
        <Grid size={{ xs: 12 }}>
          <TabContext value={tab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList onChange={handleChange} aria-label="Run details tabs">
                {data?.state === "running" && <Tab label="Live" value="live" />}
                <Tab label="Recording" value="recording" />
                <Tab label={`Flows (${flows?.length ?? 0})`} value="flows" />
                <Tab label={`Issues (${data.issues?.length ?? 0})`} value="issues" />
              </TabList>
            </Box>
            <TabPanel value="live">
              <Grid container spacing={2} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Container>
                    <LiveView
                      liveViewUrl={data.state === "running" ? data.live_view_url : undefined}
                      noLiveViewMessage={
                        data.live_view_url
                          ? "Go to the recording tab to view the video."
                          : "No live view available yet..."
                      }
                    />
                  </Container>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <DiscoveryRunLogs runId={data.id} />
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value="recording">
              <Grid container spacing={1} alignItems="flex-start">
                <Grid size={{ xs: 12, sm: 8 }}>
                  <Container>
                    {data.recording_path ? (
                      <video
                        ref={videoRef}
                        controls
                        autoPlay
                        muted
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "8px",
                          border: `1px solid ${theme.palette.grey[400]}`,
                        }}
                      >
                        <source
                          src={data.recording_path}
                          type={`video/${data.recording_path.split("?")[0].split(".").pop()}`}
                        />
                        Your browser does not support the video tag.
                      </video>
                    ) : (
                      <EmptyState message="No recording available yet..." />
                    )}
                  </Container>
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }} sx={{ height: "100%", overflowY: "auto" }}>
                  <DiscoveryRunLogs runId={data.id} />
                </Grid>
              </Grid>
            </TabPanel>
            <TabPanel value="flows">
              <Stack direction="column" spacing={2} mt={1}>
                {data.found_issues?.length > 0 && (
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Found Issues
                      </Typography>
                      <Stack direction="column" spacing={1}>
                        {data.found_issues.map((issue, index) => (
                          <Typography key={index} variant="body2">
                            {issue}
                          </Typography>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
                {data.human_input_question && (
                  <Card>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Human Input Required
                      </Typography>
                      <Typography variant="body2">{data.human_input_question}</Typography>
                    </CardContent>
                  </Card>
                )}
                {flows?.length === 0 && <EmptyState message="No user flows recorded yet." />}
              </Stack>
            </TabPanel>
            <TabPanel value="issues">
              {data.issues?.length ? (
                <Stack direction="column" spacing={2} mt={1}>
                  {data.issues.map((issue, index) => (
                    <Card key={index}>
                      <CardContent>
                        <Typography variant="body1">{issue.description}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {humanizeText(issue.issue_type)} - {humanizeText(issue.severity)}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No issues found for this discovery run.
                </Typography>
              )}
            </TabPanel>
          </TabContext>
        </Grid>
      </Grid>
    </Box>
  );
}
